import React, { useState, useEffect } from "react";
import { useOrganization } from "@/contexts/OrganizationContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, Edit, Eye, Save, X, Info, Send, FileText, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";

interface EmailTemplate {
  id: string;
  organization_id: string | null;
  template_key: string;
  name: string;
  description: string | null;
  subject_template: string;
  body_template: string;
  body_template_text: string | null;
  preheader_template: string | null;
  available_variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const SAMPLE_DATA: Record<string, Record<string, string>> = {
  team_invite: {
    invitee_name: "Sarah Johnson",
    inviter_name: "John Smith",
    team_name: "Team Alpha",
    challenge_title: "Building a Prayer App",
    accept_url: "https://buildwithchrist.ai/accept-invite/abc123",
    preheader: "Join your team for Building a Prayer App",
  },
  contact_notification: {
    reason: "Partnership Inquiry",
    first_name: "Michael",
    last_name: "Davis",
    email: "michael@example.com",
    organization: "St. Mary's Parish",
    message: "Hello, I'm interested in learning more about your platform for our parish...",
    timestamp: new Date().toLocaleString(),
    preheader: "Hello, I'm interested in learning more about your platform...",
  },
  payment_notification: {
    amount: "$500.00",
    payment_type: "Corporate Sponsorship (Gold)",
    payer_name: "Jane Wilson",
    payer_email: "jane@company.com",
    company_name: "Faith Technologies Inc.",
    timestamp: new Date().toLocaleString(),
    preheader: "$500.00 received from Jane Wilson",
  },
  registration_confirmation: {
    first_name: "John",
    last_name: "Smith",
    event_name: "Build With Christ Hackathon",
    event_date: "March 15, 2026",
    event_time: "9:00 AM",
    event_location: "Heritage Hall, Fort Worth",
    attendee_type: "Team Participant",
    team_action_url: "https://buildwithchrist.ai/challenges",
    preheader: "March 15, 2026 at Heritage Hall, Fort Worth",
  },
};

const EmailTemplates: React.FC = () => {
  const { organization } = useOrganization();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editBodyText, setEditBodyText] = useState("");
  const [editPreheader, setEditPreheader] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const organizationId = organization?.id;

  // Fetch current user's email for test emails
  useEffect(() => {
    const fetchUserEmail = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    fetchUserEmail();
  }, []);

  useEffect(() => {
    if (organizationId) {
      fetchTemplates();
    }
  }, [organizationId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // First fetch org-specific templates
      const { data: orgTemplates, error: orgError } = await supabase
        .from("email_templates")
        .select("*")
        .eq("organization_id", organizationId);

      if (orgError) throw orgError;

      // Then fetch default templates (null org_id)
      const { data: defaultTemplates, error: defaultError } = await supabase
        .from("email_templates")
        .select("*")
        .is("organization_id", null);

      if (defaultError) throw defaultError;

      // Merge: org templates override defaults by template_key
      const orgTemplateKeys = new Set((orgTemplates || []).map(t => t.template_key));
      const mergedTemplates = [
        ...(orgTemplates || []),
        ...(defaultTemplates || []).filter(t => !orgTemplateKeys.has(t.template_key)),
      ];

      setTemplates(mergedTemplates as EmailTemplate[]);
    } catch (error: any) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setEditSubject(template.subject_template);
    setEditBody(template.body_template);
    setEditBodyText(template.body_template_text || "");
    setEditPreheader(template.preheader_template || "");
  };

  const handleSave = async () => {
    if (!editingTemplate || !organizationId) return;

    setSaving(true);
    try {
      // Check if we're editing a default template (need to create org-specific copy)
      if (editingTemplate.organization_id === null) {
        // Insert new org-specific template
        const { error } = await supabase.from("email_templates").insert({
          organization_id: organizationId,
          template_key: editingTemplate.template_key,
          name: editingTemplate.name,
          description: editingTemplate.description,
          subject_template: editSubject,
          body_template: editBody,
          body_template_text: editBodyText || null,
          preheader_template: editPreheader || null,
          available_variables: editingTemplate.available_variables,
          is_active: true,
        });

        if (error) throw error;
      } else {
        // Update existing org template
        const { error } = await supabase
          .from("email_templates")
          .update({
            subject_template: editSubject,
            body_template: editBody,
            body_template_text: editBodyText || null,
            preheader_template: editPreheader || null,
          })
          .eq("id", editingTemplate.id);

        if (error) throw error;
      }

      toast.success("Email template saved successfully");
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error: any) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const replaceVariables = (text: string, templateKey: string): string => {
    const sampleData = SAMPLE_DATA[templateKey] || {};
    let result = text;
    Object.entries(sampleData).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
    });
    return result;
  };

  // Simple HTML to plain text converter for auto-generation
  const htmlToPlainText = (html: string): string => {
    let text = html;
    // Remove style and script tags
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    // Convert links to text with URL
    text = text.replace(/<a[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/gi, '$2 ($1)');
    // Replace common HTML entities
    text = text.replace(/&nbsp;/gi, ' ');
    text = text.replace(/&amp;/gi, '&');
    text = text.replace(/&lt;/gi, '<');
    text = text.replace(/&gt;/gi, '>');
    text = text.replace(/&quot;/gi, '"');
    // Convert line breaks
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n\n');
    text = text.replace(/<\/div>/gi, '\n');
    text = text.replace(/<\/tr>/gi, '\n');
    text = text.replace(/<\/h[1-6]>/gi, '\n\n');
    text = text.replace(/<\/li>/gi, '\n');
    // Remove remaining HTML tags
    text = text.replace(/<[^>]+>/g, '');
    // Normalize whitespace
    text = text.replace(/[ \t]+/g, ' ');
    text = text.replace(/\n[ \t]+/g, '\n');
    text = text.replace(/[ \t]+\n/g, '\n');
    text = text.replace(/\n{3,}/g, '\n\n');
    return text.trim();
  };

  const handleAutoGeneratePlainText = () => {
    const plainText = htmlToPlainText(editBody);
    setEditBodyText(plainText);
    toast.success("Plain text version generated from HTML");
  };

  const handleResetToDefault = async (template: EmailTemplate) => {
    if (template.organization_id === null) {
      toast.info("This is already the default template");
      return;
    }

    try {
      const { error } = await supabase
        .from("email_templates")
        .delete()
        .eq("id", template.id);

      if (error) throw error;

      toast.success("Reset to default template");
      fetchTemplates();
    } catch (error: any) {
      console.error("Error resetting template:", error);
      toast.error("Failed to reset template");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Email Templates</h1>
        <p className="text-muted-foreground mt-1">
          Customize the email copy for notifications and outreach. Changes apply immediately.
        </p>
      </div>

      {/* Best Practices Card */}
      <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-600" />
            Email Deliverability Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Multipart emails:</strong> Always include both HTML and plain text versions</li>
            <li>• <strong>Subject lines:</strong> Keep under 50 characters, avoid emojis</li>
            <li>• <strong>Preheader text:</strong> Controls inbox preview snippet (80-100 chars)</li>
            <li>• <strong>HTML structure:</strong> Table-based layout with inline CSS for compatibility</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Templates
          </CardTitle>
          <CardDescription>
            Edit subject lines, HTML body, and plain text versions. Use variables like {"{{variable_name}}"} for dynamic content.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Versions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{template.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {template.description}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {template.subject_template}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge variant="outline" className="gap-1">
                            <Code className="h-3 w-3" />
                            HTML
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>HTML version available</TooltipContent>
                      </Tooltip>
                      {template.body_template_text && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge variant="outline" className="gap-1">
                              <FileText className="h-3 w-3" />
                              Text
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>Plain text version available</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {template.organization_id ? (
                      <Badge variant="default">Customized</Badge>
                    ) : (
                      <Badge variant="secondary">Default</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreviewTemplate(template)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {template.organization_id && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetToDefault(template)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reset to default</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingTemplate?.name}</DialogTitle>
            <DialogDescription>
              Customize the email content. Both HTML and plain text versions are required for best deliverability.
            </DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-6">
              {/* Available Variables */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Available Variables</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.available_variables.map((variable) => (
                    <Badge
                      key={variable}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary/10"
                      onClick={() => {
                        navigator.clipboard.writeText(`{{${variable}}}`);
                        toast.success(`Copied {{${variable}}} to clipboard`);
                      }}
                    >
                      {`{{${variable}}}`}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Subject Line */}
              <div className="space-y-2">
                <Label>Subject Line</Label>
                <Input
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  placeholder="Email subject..."
                />
                {editSubject.length > 50 && (
                  <p className="text-xs text-amber-600">
                    Subject is {editSubject.length} chars. Consider keeping under 50 for best display.
                  </p>
                )}
              </div>

              {/* Preheader */}
              <div className="space-y-2">
                <Label>Preheader Text</Label>
                <Input
                  value={editPreheader}
                  onChange={(e) => setEditPreheader(e.target.value)}
                  placeholder="Preview text shown in inbox (80-100 chars)..."
                />
                <p className="text-xs text-muted-foreground">
                  This text appears in the inbox preview after the subject. Use variables like {"{{preheader}}"} for dynamic content.
                </p>
              </div>

              {/* Body - Tabbed HTML/Text Editor */}
              <div className="space-y-2">
                <Label>Email Body</Label>
                <Tabs defaultValue="html">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="html" className="gap-1">
                      <Code className="h-3 w-3" />
                      HTML
                    </TabsTrigger>
                    <TabsTrigger value="text" className="gap-1">
                      <FileText className="h-3 w-3" />
                      Plain Text
                    </TabsTrigger>
                    <TabsTrigger value="preview-html">Preview HTML</TabsTrigger>
                    <TabsTrigger value="preview-text">Preview Text</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="html" className="space-y-2">
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      placeholder="Email body HTML..."
                      className="min-h-[350px] font-mono text-sm"
                    />
                  </TabsContent>
                  
                  <TabsContent value="text" className="space-y-2">
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAutoGeneratePlainText}
                      >
                        Auto-generate from HTML
                      </Button>
                    </div>
                    <Textarea
                      value={editBodyText}
                      onChange={(e) => setEditBodyText(e.target.value)}
                      placeholder="Plain text version of the email..."
                      className="min-h-[350px] font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Plain text is used by email clients that don't support HTML and helps avoid spam filters.
                    </p>
                  </TabsContent>
                  
                  <TabsContent value="preview-html">
                    <div className="border rounded-lg p-4 bg-white min-h-[350px]">
                      <div className="mb-2 pb-2 border-b">
                        <span className="text-xs text-muted-foreground block">Preheader: </span>
                        <span className="text-sm">
                          {replaceVariables(editPreheader, editingTemplate.template_key) || "(none)"}
                        </span>
                      </div>
                      <div className="mb-4 pb-4 border-b">
                        <span className="text-xs text-muted-foreground block">Subject: </span>
                        <span className="font-medium">
                          {replaceVariables(editSubject, editingTemplate.template_key)}
                        </span>
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: replaceVariables(editBody, editingTemplate.template_key),
                        }}
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="preview-text">
                    <div className="border rounded-lg p-4 bg-muted/30 min-h-[350px]">
                      <div className="mb-2 pb-2 border-b">
                        <span className="text-xs text-muted-foreground block">Subject: </span>
                        <span className="font-medium">
                          {replaceVariables(editSubject, editingTemplate.template_key)}
                        </span>
                      </div>
                      <pre className="whitespace-pre-wrap font-mono text-sm">
                        {replaceVariables(editBodyText, editingTemplate.template_key) || "(No plain text version)"}
                      </pre>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Warning if no plain text */}
              {!editBodyText && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 dark:bg-amber-950/20 dark:border-amber-900">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Warning:</strong> No plain text version defined. This may hurt deliverability. 
                    Use "Auto-generate from HTML" or write a custom plain text version.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between gap-2">
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!userEmail || !editingTemplate) return;
                    setSendingTest(true);
                    try {
                      const previewSubject = replaceVariables(editSubject, editingTemplate.template_key);
                      const previewBody = replaceVariables(editBody, editingTemplate.template_key);
                      const previewBodyText = replaceVariables(editBodyText, editingTemplate.template_key);
                      
                      const response = await supabase.functions.invoke("send-test-email", {
                        body: {
                          to_email: userEmail,
                          subject: previewSubject,
                          body: previewBody,
                          body_text: previewBodyText || undefined,
                          template_name: editingTemplate.name,
                        },
                      });
                      
                      if (response.error) {
                        throw new Error(response.error.message);
                      }
                      
                      toast.success(`Test email sent to ${userEmail}`);
                    } catch (error: any) {
                      console.error("Error sending test email:", error);
                      toast.error(error.message || "Failed to send test email");
                    } finally {
                      setSendingTest(false);
                    }
                  }}
                  disabled={sendingTest || !userEmail}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendingTest ? "Sending..." : `Send Test to ${userEmail || "..."}`}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Saving..." : "Save Template"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
            <DialogDescription>
              Preview both HTML and plain text versions with sample data.
            </DialogDescription>
          </DialogHeader>

          {previewTemplate && (
            <Tabs defaultValue="html" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="html" className="gap-1">
                  <Code className="h-3 w-3" />
                  HTML Version
                </TabsTrigger>
                <TabsTrigger value="text" className="gap-1">
                  <FileText className="h-3 w-3" />
                  Plain Text Version
                </TabsTrigger>
              </TabsList>

              <TabsContent value="html" className="space-y-4">
                {previewTemplate.preheader_template && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <span className="text-xs text-muted-foreground block mb-1">Preheader (inbox preview): </span>
                    <span className="text-sm">
                      {replaceVariables(previewTemplate.preheader_template, previewTemplate.template_key)}
                    </span>
                  </div>
                )}
                <div className="bg-muted/50 rounded-lg p-3">
                  <span className="text-xs text-muted-foreground block mb-1">Subject: </span>
                  <span className="font-medium">
                    {replaceVariables(previewTemplate.subject_template, previewTemplate.template_key)}
                  </span>
                </div>
                <div className="border rounded-lg p-4 bg-white">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: replaceVariables(
                        previewTemplate.body_template,
                        previewTemplate.template_key
                      ),
                    }}
                  />
                </div>
              </TabsContent>

              <TabsContent value="text" className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-3">
                  <span className="text-xs text-muted-foreground block mb-1">Subject: </span>
                  <span className="font-medium">
                    {replaceVariables(previewTemplate.subject_template, previewTemplate.template_key)}
                  </span>
                </div>
                <div className="border rounded-lg p-4 bg-muted/30">
                  {previewTemplate.body_template_text ? (
                    <pre className="whitespace-pre-wrap font-mono text-sm">
                      {replaceVariables(
                        previewTemplate.body_template_text,
                        previewTemplate.template_key
                      )}
                    </pre>
                  ) : (
                    <p className="text-muted-foreground italic">
                      No plain text version available. Edit this template to add one.
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
